// The participant's final alignment (Spec "Optional own-score revision -> Final alignment"), under the level's rule
// (LevelRule, DC-36). B14 (R5, C4, DC-249): the tiles are the room's offered set (`debrief.open.level.offered`) in its
// display order — Strong Hire · Hire · Consider Other Role · No Hire on a playbook requisition, Hire · No Hire on a
// level-v1 one, the earlier scale's three on a legacy one — with the words of the design system's mapping (C5). The
// row starts from the participant's current recommendation without writing it (D9, Q-B7-4); Align writes it. Under a
// binding No Hire every tile but No Hire is blocked with his rule (the server refuses any other value).
import { useState } from "react";
import { ACTION_LABELS, Banner, Button, COMMON_LABELS, DEBRIEF_STATUS, LEVEL_RULE_LABELS, LevelRule, RadioTile, RECOMMENDATION_LABELS, Stack } from "@/mc";
import type { RadioTileOption } from "@/mc";
import { debriefSetAlignment, type DebriefSetAlignmentInput } from "@/api/tools";
import { errorText, useWrite } from "@/api/useTool";
import { levelOf, recoWord } from "@/views/b7/room/reco";
import type { RoomPanelProps } from "@/views/b7/room/types";
import { bandOf, memberOf, ROOM_KEYS } from "./labels";

/** The tiles: the offered set in its order, each in the mapping's words (an earlier-scale value with its marker, on a
 * legacy room); under a bound No Hire only No Hire is open. */
export function alignmentOptions(offered: readonly string[], bound: boolean, reason: string): RadioTileOption[] {
  return offered.flatMap((value) => {
    const word = recoWord(value);
    if (!word) return [];
    return [{
      value, label: word.label, sub: word.earlierScale ? RECOMMENDATION_LABELS.earlierScale : undefined,
      blocked: bound && value !== "no_hire" ? { reason } : undefined,
    }];
  });
}

export function Alignment({ view, applicationId, onChanged }: RoomPanelProps & { scorecardId?: string | undefined }) {
  const write = useWrite(debriefSetAlignment, ROOM_KEYS);
  const member = memberOf(view, applicationId);
  const mine = member?.alignments.find((a) => a.mine === true);
  const { offered } = levelOf(view);
  const isValue = (v: unknown): v is string => typeof v === "string" && offered.includes(v);
  const recorded = isValue(mine?.value) ? mine.value : null;
  const current = member?.my_scorecard?.recommendation;
  const [value, setValue] = useState<string>(recorded ?? (isValue(current) ? current : ""));
  const [refused, setRefused] = useState<string | null>(null);
  if (!member || !mine) return null;
  const band = bandOf(view);
  const bound = member.bound_no_hire === true || member.alignments.some((a) => a.bound_no_hire === true);
  const open = member.can.set_alignment === true;
  const options = alignmentOptions(offered, bound, LEVEL_RULE_LABELS.vpRule);
  // under a binding No Hire the only value the server takes is No Hire: it is the row's value whatever was chosen
  const chosen = bound && value !== "no_hire" ? (offered.includes("no_hire") ? "no_hire" : "") : value;
  const align = async () => {
    if (!chosen) return;
    setRefused(null);
    try {
      await write.mutateAsync({
        requisition_id: view.requisition_id, cycle_id: view.cycle_id, application_id: applicationId,
        value: chosen as DebriefSetAlignmentInput["value"],
      });
      onChanged();
    } catch (e) {
      setRefused(errorText(e));
    }
  };
  return (
    <Stack gap={12}>
      {band !== "legacy" ? <LevelRule level={band === "vp_plus" ? "vp_plus" : "sr_director_and_below"} met={bound} /> : null}
      <RadioTile
        label={DEBRIEF_STATUS.alignment}
        options={options}
        value={chosen}
        onChange={(v) => { if (isValue(v) && !(bound && v !== "no_hire")) setValue(v); }}
        row
        mode={open ? "edit" : "view"}
      />
      <Button
        tone="primary"
        
        mode={open ? "edit" : "view"}
        busy={write.isPending}
        blocked={chosen ? undefined : { reason: COMMON_LABELS.required }}
        onClick={() => void align()}
      >
        {ACTION_LABELS.align}
      </Button>
      {refused ? <Banner tone="red" live>{refused}</Banner> : null}
    </Stack>
  );
}
